import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { getInitials, getRandomColor, formatPhoneNumber } from '../../utils/helpers';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getAttendance,
  getAttendanceStats,
  getAtRiskMembers,
  getMemberAttendanceHistory,
  checkInMember,
  bulkCheckInMembers,
  closeCheckInWindow,
  exportAttendance,
  clearApiMessage,
  clearError,
  clearAttendanceState,
  selectAttendanceRecords,
  selectAtRiskMembers,
  selectAttendanceStats,
  selectAttendancePagination,
  selectAttendanceLoading,
  selectAttendanceError,
  selectAttendanceApiMessage,
  selectAttendanceSuccess,
  selectAttendanceExportData,
  selectAttendanceStatsSummary,
  selectAtRiskCount,
} from '../../store/slices/engagement/attendanceSlice';
import {
  Download,
  BarChart3,
  FileJson,
  TrendingUp,
  TrendingDown,
  FileText,
  AlertTriangle,
  Send,
  User,
  Pencil,
  Church,
  Calendar,
  Smartphone,
  CheckCircle2,
  Search,
  Eye,
  ClipboardList,
} from 'lucide-react';

// ==================== HELPER: Format Date ====================
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }
    return dateString;
  } catch (e) {
    return dateString;
  }
};

const Attendance = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedWeeks, setSelectedWeeks] = useState('12');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const records = useSelector(selectAttendanceRecords) || [];
  const atRiskMembers = useSelector(selectAtRiskMembers) || [];
  const stats = useSelector(selectAttendanceStatsSummary);
  const pagination = useSelector(selectAttendancePagination);
  const loading = useSelector(selectAttendanceLoading);
  const apiMessage = useSelector(selectAttendanceApiMessage);
  const error = useSelector(selectAttendanceError);
  const successMessage = useSelector(selectAttendanceSuccess);
  const atRiskCount = useSelector(selectAtRiskCount);
  const exportData = useSelector(selectAttendanceExportData);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getAttendanceStats());
        await dispatch(getAtRiskMembers({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm,
          group: groupFilter
        }));
      } catch (err) {
        console.error('Error loading attendance data:', err);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearAttendanceState());
    };
  }, [dispatch]);

  // Handle filters and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (groupFilter) params.group = groupFilter;
      
      dispatch(getAtRiskMembers(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, groupFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages
  useEffect(() => {
    const message = apiMessage || successMessage;
    if (!message || !showToast) return;
    
    const messageKey = `${message}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(message, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, successMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== PAGINATION ====================
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;
  const perPage = pagination?.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // Get unique groups for filter
  const uniqueGroups = atRiskMembers.length > 0 
    ? [...new Set(atRiskMembers.map(m => m.group))].filter(Boolean)
    : [];

  // ==================== EXPORT FUNCTIONS ====================
  const handleExport = async (exportType, memberId = null, format = 'csv') => {
    try {
      setIsSubmitting(true);
      
      // Build params for the API call
      const params = {};
      if (memberId) params.member_id = memberId;
      
      // Map export types to API types
      let apiType = exportType;
      if (exportType === 'at-risk') apiType = 'at-risk';
      else if (exportType === 'summary') apiType = 'summary';
      else if (exportType === 'weekly') apiType = 'weekly';
      else if (exportType === 'single') apiType = 'single';
      else apiType = 'all';
      
      const result = await dispatch(exportAttendance({ 
        type: apiType, 
        params 
      })).unwrap();
      
      console.log('Export result:', result);
      
      if (result.success && result.exportData) {
        let data = result.exportData;
        
        // Extract the actual data array
        if (data.data && Array.isArray(data.data)) {
          data = data.data;
        } 
        else if (typeof data === 'object' && !Array.isArray(data)) {
          if (data.data && Array.isArray(data.data)) {
            data = data.data;
          } else {
            // If it's a single object, wrap it in an array
            data = [data];
          }
        }
        
        // Ensure we have an array
        if (!Array.isArray(data)) {
          data = [data];
        }
        
        if (data.length === 0) {
          showToastMessage('No data to export', 'error');
          return;
        }
        
        const filename = getExportFilename(`attendance-${exportType}`);
        const title = `Attendance Report - ${exportType}`;
        
        // Execute export based on format
        switch (format.toLowerCase()) {
          case 'csv':
            exportToCSV(data, filename);
            break;
          case 'json':
            exportToJSON(data, filename);
            break;
          case 'excel':
            exportToExcel(data, filename);
            break;
          case 'pdf':
            exportToPDF(data, title, filename);
            break;
          default:
            showToastMessage('Unsupported export format', 'error');
            return;
        }
        
        showToastMessage(`📥 ${data.length} records exported as ${format.toUpperCase()}!`, 'success');
        closeModal();
      } else {
        showToastMessage('No data to export', 'error');
      }
    } catch (err) {
      console.error('Export error:', err);
      showToastMessage(err.message || 'Failed to export data', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Export Modal with format selection
  const openExportModal = (exportType = 'all', member = null) => {
    let title = 'Export Attendance Report';
    let description = 'Export attendance data in your preferred format.';
    let dataCount = 'all';

    switch (exportType) {
      case 'single':
        if (member) {
          title = `Export Member: ${member.name}`;
          description = `Export data for "${member.name}" in your preferred format.`;
          dataCount = '1';
        }
        break;
      case 'at-risk':
        title = 'Export At-Risk Members';
        description = `Export ${atRiskMembers.length} at-risk members in your preferred format.`;
        dataCount = atRiskMembers.length;
        break;
      case 'summary':
        title = 'Export Summary Report';
        description = 'Export attendance summary statistics in your preferred format.';
        dataCount = 'summary';
        break;
      case 'weekly':
        title = 'Export Weekly Attendance';
        description = 'Export weekly attendance trend data in your preferred format.';
        dataCount = statsData.weeklyData?.length || 0;
        break;
      default:
        title = 'Export All Attendance';
        description = 'Export all attendance data in your preferred format.';
        dataCount = 'all';
        break;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            {description}
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleExport(exportType, member?.id, format)}
                  disabled={isSubmitting}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <FileJson size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-3px' }} /> {dataCount} records will be exported</span>
          </div>

          {/* Quick Export Options */}
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Quick Export Options
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={async () => {
                  try {
                    setIsSubmitting(true);
                    const result = await dispatch(exportAttendance({ 
                      type: 'summary', 
                      params: {} 
                    })).unwrap();
                    if (result.success && result.exportData) {
                      let data = result.exportData;
                      if (data.data && Array.isArray(data.data)) {
                        data = data.data;
                      } else if (typeof data === 'object' && !Array.isArray(data)) {
                        data = [data];
                      }
                      if (data.length > 0) {
                        exportToCSV(data, getExportFilename('attendance-summary'));
                        showToastMessage(`📥 Summary exported as CSV!`, 'success');
                        closeModal();
                      }
                    }
                  } catch (err) {
                    showToastMessage(err.message || 'Failed to export', 'error');
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                disabled={isSubmitting}
              >
                <ClipboardList size={14} style={{ verticalAlign: '-2px' }} /> Summary Only
              </button>
              {atRiskMembers.length > 0 && (
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={async () => {
                    try {
                      setIsSubmitting(true);
                      const result = await dispatch(exportAttendance({ 
                        type: 'at-risk', 
                        params: {} 
                      })).unwrap();
                      if (result.success && result.exportData) {
                        let data = result.exportData;
                        if (data.data && Array.isArray(data.data)) {
                          data = data.data;
                        } else if (typeof data === 'object' && !Array.isArray(data)) {
                          data = [data];
                        }
                        if (data.length > 0) {
                          exportToCSV(data, getExportFilename('at-risk-quick'));
                          showToastMessage(`📥 At-risk members exported as CSV!`, 'success');
                          closeModal();
                        }
                      }
                    } catch (err) {
                      showToastMessage(err.message || 'Failed to export', 'error');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> At-Risk Only
                </button>
              )}
              {statsData.weeklyData && statsData.weeklyData.length > 0 && (
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={async () => {
                    try {
                      setIsSubmitting(true);
                      const result = await dispatch(exportAttendance({ 
                        type: 'weekly', 
                        params: {} 
                      })).unwrap();
                      if (result.success && result.exportData) {
                        let data = result.exportData;
                        if (data.data && Array.isArray(data.data)) {
                          data = data.data;
                        } else if (typeof data === 'object' && !Array.isArray(data)) {
                          data = [data];
                        }
                        if (data.length > 0) {
                          exportToCSV(data, getExportFilename('attendance-weekly'));
                          showToastMessage(`📥 Weekly data exported as CSV!`, 'success');
                          closeModal();
                        }
                      }
                    } catch (err) {
                      showToastMessage(err.message || 'Failed to export', 'error');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> Weekly Only
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // Export handlers
  const handleExportAtRisk = () => {
    if (atRiskMembers.length === 0) {
      showToastMessage('No at-risk members to export', 'error');
      return;
    }
    openExportModal('at-risk');
  };

  const handleExportSummary = () => {
    openExportModal('summary');
  };

  const handleExportWeekly = () => {
    if (!statsData.weeklyData || statsData.weeklyData.length === 0) {
      showToastMessage('No weekly data to export', 'error');
      return;
    }
    openExportModal('weekly');
  };

  const handleExportAll = () => {
    openExportModal('all');
  };

  const handleExportMember = (member) => {
    openExportModal('single', member);
  };

  // ==================== ATTENDANCE HANDLERS ====================

  const handleCheckIn = async (member) => {
    try {
      setIsSubmitting(true);
      await dispatch(checkInMember({
        member_id: member.id,
        service_type: 'sunday',
        check_in_method: 'manual'
      })).unwrap();
      showToastMessage(`📨 Check-in SMS sent to ${member.name}!`, 'success');
      await dispatch(getAtRiskMembers({ 
        per_page: itemsPerPage, 
        page: currentPage,
        search: searchTerm,
        group: groupFilter
      }));
      await dispatch(getAttendanceStats());
    } catch (err) {
      showToastMessage(err.message || `Failed to check in ${member.name}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAllCheckIn = async () => {
    if (atRiskMembers.length === 0) {
      showToastMessage('No at-risk members to check in', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      const memberIds = atRiskMembers.map(m => m.id);
      await dispatch(bulkCheckInMembers({
        member_ids: memberIds,
        service_type: 'sunday',
        check_in_method: 'manual'
      })).unwrap();
      showToastMessage(`📨 Check-in SMS sent to ${memberIds.length} members!`, 'success');
      await dispatch(getAtRiskMembers({ 
        per_page: itemsPerPage, 
        page: currentPage,
        search: searchTerm,
        group: groupFilter
      }));
      await dispatch(getAttendanceStats());
    } catch (err) {
      showToastMessage(err.message || 'Failed to send check-ins', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseWindow = async () => {
    try {
      setIsSubmitting(true);
      await dispatch(closeCheckInWindow()).unwrap();
      showToastMessage('Check-in window closed', 'info');
    } catch (err) {
      showToastMessage(err.message || 'Failed to close check-in window', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== VIEW PROFILE MODAL ====================

  const handleViewProfile = (member) => {
    const color = getRandomColor(member.id);
    const statusBadge = member.status === 'Active' ? 'badge-green' : member.status === 'Inactive' ? 'badge-red' : 'badge-amber';
    const scoreColor = member.engagement_score >= 80 ? 'var(--green)' : member.engagement_score >= 50 ? 'var(--amber)' : 'var(--red)';

    openModal(
      <div>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div className="member-row-avatar" style={{ background: color, width: '50px', height: '50px', fontSize: '18px' }}>
              {getInitials(member.name)}
            </div>
            <div>
              <div className="modal-title" style={{ fontSize: '18px' }}>{member.name}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                {formatPhoneNumber(member.phone)} · {member.group}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          {/* Quick Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
            <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{member.engagement_score || 0}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Engagement</div>
            </div>
            <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{formatDate(member.created_at)}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Joined</div>
            </div>
            <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{member.branch || 'N/A'}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Branch</div>
            </div>
            <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
                <span className={`badge ${statusBadge}`}>{member.status || 'Active'}</span>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Status</div>
            </div>
          </div>

          {/* Member Details */}
          <div className="grid-2" style={{ gap: '12px', marginBottom: '16px' }}>
            {[
              ['Full Name', member.name],
              ['Phone', formatPhoneNumber(member.phone)],
              ['Email', member.email || 'N/A'],
              ['Date of Birth', member.dob ? formatDate(member.dob) : 'N/A'],
              ['Gender', member.gender === 'M' ? 'Male' : member.gender === 'F' ? 'Female' : 'N/A'],
              ['Group', member.group || 'N/A'],
              ['Branch', member.branch || 'N/A'],
              ['Status', `<span class="badge ${statusBadge}">${member.status || 'Active'}</span>`]
            ].map(([l, v]) => (
              <div key={l} style={{ padding: '10px 12px', background: 'var(--cream)', borderRadius: '8px' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-light)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{l}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }} dangerouslySetInnerHTML={{ __html: v }}></div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button 
              className="btn btn-gold btn-sm" 
              onClick={() => {
                handleCheckIn(member);
                closeModal();
              }}
              title={`Send check-in SMS to ${member.name}`}
              disabled={isSubmitting}
            >
              <Send size={14} style={{ verticalAlign: '-2px' }} /> Send Check-in
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                handleExportMember(member);
                closeModal();
              }}
              title={`Export ${member.name}'s data`}
              disabled={isSubmitting}
            >
              <Download size={14} style={{ verticalAlign: '-2px' }} /> Export
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                showToastMessage(`👤 Viewing ${member.name}'s full profile`, 'info');
                closeModal();
              }}
              title={`View ${member.name}'s full profile`}
            >
              <User size={14} style={{ verticalAlign: '-2px' }} /> Full Profile
            </button>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              closeModal();
              showToastMessage(`✏️ Editing ${member.name}`, 'info');
            }}
          >
            <Pencil size={14} style={{ verticalAlign: '-2px' }} /> Edit Member
          </button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  const statsData = stats || {
    lastSunday: 0,
    monthAverage: 0,
    smsCheckIns: 0,
    atRiskCount: 0,
    totalMembers: 0,
    attendanceRate: 0,
    weeklyData: []
  };

  // Use atRiskMembers from Redux
  const currentItems = atRiskMembers;

  return (
    <>
      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">Last Sunday</div>
          <div className="stat-value">{statsData.lastSunday}</div>
          <div className="stat-change up">↑ {statsData.attendanceRate}% rate</div>
          <div className="stat-icon"><Church size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">This Month Avg</div>
          <div className="stat-value">{statsData.monthAverage}</div>
          <div className="stat-change up">↑ weekly average</div>
          <div className="stat-icon"><Calendar size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card blue" style={{ cursor: 'default' }}>
          <div className="stat-label">SMS Check-ins</div>
          <div className="stat-value">{statsData.smsCheckIns}</div>
          <div className="stat-change up">{statsData.attendanceRate}% of attendance</div>
          <div className="stat-icon"><Smartphone size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card purple" style={{ cursor: 'default' }}>
          <div className="stat-label">At-Risk Members</div>
          <div className="stat-value">{statsData.atRiskCount}</div>
          <div className="stat-change down">absent 14+ days</div>
          <div className="stat-icon"><AlertTriangle size={28} strokeWidth={1.5} /></div>
        </div>
      </div>

      <div className="grid-65-35" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div className="card-header">
            <div className="card-title"><BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Weekly Attendance Trend</div>
            <div style={{ width: 'auto', padding: '25px 10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <select 
                className="form-select" 
                style={{ width: 'auto', padding: '6px 10px', fontSize: '12px' }}
                value={selectedWeeks}
                onChange={(e) => setSelectedWeeks(e.target.value)}
                title="Select number of weeks to display"
                disabled={loading}
              >
                <option value="6">Last 6 weeks</option>
                <option value="12">Last 12 weeks</option>
                <option value="24">Last 24 weeks</option>
              </select>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={handleExportWeekly}
                title="Export weekly attendance data"
                disabled={isSubmitting}
              >
                <Download size={14} style={{ verticalAlign: '-2px' }} /> Export
              </button>
            </div>
          </div>
          <div className="bar-chart" style={{ height: '130px', paddingTop: '10px' }}>
            {statsData.weeklyData && statsData.weeklyData.length > 0 ? (
              statsData.weeklyData.slice(-parseInt(selectedWeeks)).map((v, i, arr) => {
                const total = Math.max(...statsData.weeklyData, 1);
                const isLast = i === arr.length - 1;
                const opacity = isLast ? 1 : 0.4 + (i / arr.length) * 0.6;
                return (
                  <div key={i} className="bar-col">
                    <div style={{ fontSize: '9px', color: 'var(--text-light)', marginBottom: '3px' }}>{v}</div>
                    <div className="bar" style={{ 
                      height: `${(v / total) * 110}px`, 
                      background: isLast ? 'var(--gold)' : 'var(--navy)', 
                      opacity: opacity 
                    }}></div>
                    <div className="bar-label">W{i + 1}</div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
                No attendance data available
              </div>
            )}
          </div>
          <div style={{ 
            marginTop: '12px', 
            padding: '10px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
            fontSize: '12px',
            color: 'var(--text-mid)'
          }}>
            <span><TrendingUp size={14} style={{ verticalAlign: '-3px' }} /> Average attendance: <strong>{statsData.monthAverage}</strong></span>
            <span><BarChart3 size={14} style={{ verticalAlign: '-3px' }} /> Total members: <strong>{statsData.totalMembers}</strong></span>
            <span><TrendingDown size={14} style={{ verticalAlign: '-3px' }} /> At-risk: <strong>{statsData.atRiskCount}</strong></span>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title"><Smartphone size={16} style={{ verticalAlign: '-3px' }} /> SMS Check-in Setup</div>
          </div>
          <div style={{ background: 'var(--cream)', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '6px' }}>Members text to check in:</div>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>IN → 40404</div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>or text PRESENT · HERE</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', background: 'var(--green-bg)', borderRadius: '8px', marginBottom: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--green)' }}><CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Check-in window is OPEN</span>
            <button 
              className="btn btn-danger btn-sm" 
              onClick={handleCloseWindow} 
              title="Close check-in window"
              disabled={isSubmitting}
            >
              Close
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button 
              className="btn btn-gold btn-sm" 
              style={{ flex: 1 }} 
              onClick={handleExportSummary}
              title="Download summary report"
              disabled={isSubmitting}
            >
              <Download size={14} style={{ verticalAlign: '-2px' }} /> Summary Report
            </button>
            <button
              className="btn btn-ghost btn-sm"
              style={{ flex: 1 }}
              onClick={handleExportAll}
              title="Download detailed report"
              disabled={isSubmitting}
            >
              <BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> Detailed Report
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
          <div className="card-title"><AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> At-Risk Members — Absent 14+ Days</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button 
              className="btn btn-gold btn-sm" 
              onClick={handleSendAllCheckIn}
              title={`Send check-in SMS to ${currentItems.length} members`}
              disabled={isSubmitting || currentItems.length === 0}
            >
              <Send size={14} style={{ verticalAlign: '-2px' }} /> Send to All ({currentItems.length})
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleExportAtRisk}
              title="Export at-risk members list"
              disabled={isSubmitting || currentItems.length === 0}
            >
              <Download size={14} style={{ verticalAlign: '-2px' }} /> Export List
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '12px', 
          marginBottom: '16px',
          padding: '12px',
          background: 'var(--cream)',
          borderRadius: '8px'
        }}>
          <div className="search-box" style={{ maxWidth: '250px', flex: '1' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input 
              type="text" 
              placeholder="Search members…" 
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search members by name or phone"
            />
          </div>
          <select 
            className="form-select" 
            style={{ width: '150px' }}
            value={groupFilter}
            onChange={(e) => {
              setGroupFilter(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by group"
            disabled={loading}
          >
            <option value="">All Groups</option>
            {uniqueGroups.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <div style={{ fontSize: '12px', color: 'var(--text-light)', display: 'flex', alignItems: 'center' }}>
            {totalItems} member{totalItems !== 1 ? 's' : ''} at risk
          </div>
        </div>

        <div className="table-wrap">
          {loading && currentItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>⏳</div>
              <div style={{ color: 'var(--text-light)' }}>Loading members...</div>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Last Seen</th>
                  <th>Group</th>
                  <th>Engagement</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((m) => {
                    const color = getRandomColor(m.id);
                    const lastSeen = m.last_seen_at || m.updated_at || '3 weeks ago';
                    return (
                      <tr key={m.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="member-row-avatar" style={{ background: color, width: '28px', height: '28px', fontSize: '11px' }}>
                              {getInitials(m.name)}
                            </div>
                            <div>
                              <span style={{ fontWeight: '500' }}>{m.name}</span>
                              <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>{m.phone}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize: '12px', color: 'var(--text-light)' }}>{formatDate(lastSeen)}</td>
                        <td><span className="badge badge-navy" style={{ fontSize: '10px' }}>{m.group || 'General'}</span></td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="progress-bar" style={{ width: '60px' }}>
                              <div className="progress-fill" style={{ width: `${m.engagement_score || 0}%`, background: 'var(--red)' }}></div>
                            </div>
                            <span style={{ fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', color: 'var(--red)' }}>{m.engagement_score || 0}</span>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            <button 
                              className="btn btn-ghost btn-sm" 
                              onClick={() => handleCheckIn(m)}
                              title={`Send check-in SMS to ${m.name}`}
                              disabled={isSubmitting}
                            >
                              <Send size={14} style={{ verticalAlign: '-2px' }} /> Check In
                            </button>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleViewProfile(m)}
                              title={`View ${m.name}'s profile`}
                              disabled={isSubmitting}
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleExportMember(m)}
                              title={`Export ${m.name}'s data`}
                              disabled={isSubmitting}
                            >
                              <Download size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5">
                      <div className="empty-state">
                        <div className="empty-icon"><CheckCircle2 size={40} strokeWidth={1.5} /></div>
                        <div className="empty-title">No at-risk members found</div>
                        <div className="empty-text">All members are engaged and active!</div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
            paddingTop: '14px',
            borderTop: '1px solid var(--border)',
            marginTop: '4px'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
              Showing <strong>{((currentPageNum - 1) * perPage) + 1}</strong> to <strong>{Math.min(currentPageNum * perPage, totalItems)}</strong> of <strong>{totalItems}</strong> at-risk members
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                <select 
                  className="form-select" 
                  style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  title="Items per page"
                  disabled={loading}
                >
                  <option value={3}>3</option>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', gap: '4px' }}>
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={goToPreviousPage}
                  disabled={currentPageNum === 1 || loading}
                  style={{ 
                    opacity: currentPageNum === 1 ? 0.5 : 1, 
                    cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer' 
                  }}
                  title="Previous page"
                >
                  ← Prev
                </button>
                
                {getPageNumbers().map(page => (
                  <button 
                    key={page}
                    className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => goToPage(page)}
                    title={`Go to page ${page}`}
                    disabled={loading}
                  >
                    {page}
                  </button>
                ))}
                
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPageNum === totalPages || loading}
                  style={{ 
                    opacity: currentPageNum === totalPages ? 0.5 : 1, 
                    cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer' 
                  }}
                  title="Next page"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .grid-65-35 {
            grid-template-columns: 1fr !important;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
          .table-wrap {
            overflow-x: auto;
          }
          .table-wrap table {
            min-width: 600px;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
          .bar-chart {
            height: 100px !important;
          }
          .bar-chart .bar-col .bar-label {
            font-size: 8px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .member-row-avatar {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          flex-shrink: 0;
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-green {
          background: var(--green);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-red {
          background: var(--red);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-amber {
          background: var(--amber);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .progress-bar {
          height: 6px;
          background: var(--border);
          border-radius: 3px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.6s ease;
        }
        .bar-chart {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          height: 130px;
          padding-top: 10px;
        }
        .bar-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
        }
        .bar {
          width: 100%;
          border-radius: 3px 3px 0 0;
          transition: height 0.3s ease;
          min-height: 2px;
        }
        .bar-label {
          font-size: 9px;
          color: var(--text-light);
          margin-top: 3px;
        }
      `}</style>
    </>
  );
};

export default Attendance;